import { useMemo, useRef, type KeyboardEvent, type PointerEvent } from 'react';
import type { RoadmapLevelStage } from 'Selectors/curriculum';
import type { Point } from './geometry';

/** Linear interpolation through increasing knots, clamped at both ends. */
function interpolate(from: number[], to: number[], value: number) {
  if (value <= from[0]) return to[0];
  const index = from.findIndex((knot) => knot >= value);
  if (index < 0) return to[to.length - 1];
  const span = from[index] - from[index - 1] || 1;
  return to[index - 1] + ((value - from[index - 1]) / span) * (to[index] - to[index - 1]);
}

/**
 * Evenly sized lesson segments stay aligned with their lessons, even where open
 * repertoire stages widen the gaps, by mapping world positions through lesson centres.
 */
function trackScale(points: Point[], worldWidth: number) {
  const world = [0, ...points.map((point) => point.x), worldWidth];
  const track = [0, ...points.map((_, i) => (i + 0.5) / points.length), 1];
  return {
    toTrack: (x: number) => interpolate(world, track, x),
    toWorld: (t: number) => interpolate(track, world, t),
  };
}

export function RoadmapMinimap({
  graph,
  points,
  worldWidth,
  viewportWidth,
  scroll,
  selectedLevel,
  disabled,
  onScroll,
}: {
  graph: RoadmapLevelStage[];
  points: Point[];
  worldWidth: number;
  viewportWidth: number;
  scroll: number;
  selectedLevel: number;
  disabled: boolean;
  onScroll: (left: number) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const { toTrack, toWorld } = useMemo(() => trackScale(points, worldWidth), [points, worldWidth]);
  const maxScroll = Math.max(0, worldWidth - viewportWidth);
  const start = toTrack(scroll);
  const end = toTrack(Math.min(worldWidth, scroll + viewportWidth));
  const center = scroll + viewportWidth / 2;
  const centered = points.reduce(
    (best, point, i) => (Math.abs(point.x - center) < Math.abs(points[best].x - center) ? i : best),
    0,
  );
  const completeCount = graph.filter((stage) => stage.lessonState === 'complete').length;
  const percent = graph.length ? Math.round((completeCount / graph.length) * 100) : 0;
  const inactive = disabled || maxScroll === 0 || !points.length;

  const scrollToCenter = (x: number) =>
    onScroll(Math.min(maxScroll, Math.max(0, x - viewportWidth / 2)));
  const scrollToPointer = (clientX: number) => {
    const bounds = trackRef.current?.getBoundingClientRect();
    if (bounds?.width) scrollToCenter(toWorld((clientX - bounds.left) / bounds.width));
  };
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (inactive || event.button !== 0) return;
    dragging.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    scrollToPointer(event.clientX);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step: Record<string, number> = {
      ArrowRight: 1,
      ArrowUp: 1,
      ArrowLeft: -1,
      ArrowDown: -1,
      PageUp: 5,
      PageDown: -5,
      Home: -graph.length,
      End: graph.length,
    };
    if (inactive || !(event.key in step)) return;
    event.preventDefault();
    event.stopPropagation();
    const target = Math.min(points.length - 1, Math.max(0, centered + step[event.key]));
    scrollToCenter(points[target].x);
  };

  return (
    <div className="rm-minimap" data-testid="roadmap-minimap">
      <div
        className="rm-minimap-track"
        role="slider"
        tabIndex={inactive ? -1 : 0}
        aria-label="Scroll curriculum map"
        aria-disabled={inactive}
        aria-valuemin={1}
        aria-valuemax={graph.length}
        aria-valuenow={centered + 1}
        aria-valuetext={`Level ${graph[centered]?.levelNumber ?? ''} in view, ${centered + 1} of ${graph.length}`}
        onPointerDown={onPointerDown}
        onPointerMove={(event) => dragging.current && scrollToPointer(event.clientX)}
        onPointerUp={() => {
          dragging.current = false;
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
        onKeyDown={onKeyDown}
      >
        <div className="rm-minimap-rail" ref={trackRef} aria-hidden="true">
          {graph.map((stage) => (
            <i
              key={stage.levelNumber}
              className={`is-${stage.lessonState} ${stage.levelNumber === selectedLevel ? 'is-selected' : ''}`}
            />
          ))}
          {!inactive && (
            <span
              className="rm-minimap-lens"
              style={{
                left: `calc(${start * 100}% - 5px)`,
                width: `calc(${Math.max(0, end - start) * 100}% + 10px)`,
              }}
            />
          )}
        </div>
      </div>
      <p className="rm-minimap-caption">
        <strong>{percent}% complete</strong>
        <span>
          {completeCount} of {graph.length} lessons
        </span>
      </p>
    </div>
  );
}
